import { useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback } from "react";
import type { GroupBase, SelectInstance } from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import { assertPresence } from "utils/assertions";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { BrandFilter } from "__generated__/gql/graphql";
import { combineFilters } from "utils/useCombineFilters";

const optionFragment = gql(/* GraphQL */ `
  fragment BrandSelectOption on Brand {
    id
    name
    website
  }
`);

const valueQuery = gql(/* GraphQL */ `
  query BrandSelectValue($ids: [UUID!]!) {
    brandsList(filter: { id: { in: $ids } }) {
      ...BrandSelectOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query BrandSelectOptions($filter: BrandFilter, $limit: Int = 5) {
    brands(filter: $filter, orderBy: NAME_ASC, first: $limit) {
      totalCount
      nodes {
        ...BrandSelectOption
      }
    }
  }
`);

export type Option = DocumentType<typeof optionFragment>;

function noOptionsMessage({ inputValue }: { inputValue: string }) {
  if (inputValue?.length > 1)
    return <>No brands found containing: “{inputValue}”</>;
  return "Enter at least two characters to search…";
}

export type Props<IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    filter?: BrandFilter;
    isAdmin?: boolean;
    themingProps?: SelectThemingProps;
  };

function BrandSelectInner<IsMulti extends boolean = false>(
  {
    filter: filterProp,
    isAdmin,
    isMulti,
    onChange,
    value: idValue,
    styles,
    themingProps,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);
  const client = useApolloClient();

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.brandsList),
    isMulti,
    labelKey: "name",
    onChange,
    query: valueQuery,
    queryOptions: { context: ADMIN_CONTEXT },
    value: idValue,
  });

  const asyncFetchProps = useAsyncFetchOptions({
    fetchOptions: useCallback(
      (inputValue) => {
        const filter = combineFilters(filterProp, {
          name: { includesInsensitive: inputValue },
        });
        return client
          .query({
            query,
            context: isAdmin ? ADMIN_CONTEXT : undefined,
            variables: { filter },
          })
          .then(({ data }) => {
            assertPresence(data.brands, "Could not get brands from response.");
            const { nodes, totalCount } = data.brands;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [client, filterProp, isAdmin],
    ),
  });

  return (
    <AsyncSelect<Option, IsMulti>
      {...{
        ...styleProps,
        ...idValueProps,
        ...asyncFetchProps,
        ref,
        isMulti,
        noOptionsMessage,
      }}
      components={{
        ...styleProps.components,
        ...asyncFetchProps.components,
        ...rest.components,
      }}
      {...rest}
    />
  );
}

export default forwardRef(BrandSelectInner);
